// How the app works — the guide (Sep 2026, user ask: "documents for the app:
// how it works, how to change backing options, how the bass works, how the
// reharm works, the voice-leading options and the chord melody, recommended
// reading… pick a tune, apply some settings, then link to the example").
//
// Every example is a workbench link carrying a RECIPE (tuneRecipe.ts): the
// tune, then the settings this section is about, applied on arrival. The
// prose stays short and points at the control by its visible name; the depth
// lives in the pages it links to and in the books at the end.

import { Link } from 'react-router-dom';
import { recipeUrl, type TuneRecipe } from '@/components/tuneRecipe';
import { APP_ROUTES } from '@/navigation';
import '@/learn.css';

interface Example {
  title: string;
  note: string;
  recipe: TuneRecipe;
  tune?: string;
  read?: 'bb' | 'eb' | 'c-bass';
}

function Examples({ items }: { items: Example[] }) {
  return (
    <div className="learn-index-list guide-examples" data-testid="guide-examples">
      {items.map((e) => (
        <Link
          key={e.title + e.note}
          to={recipeUrl({ title: e.tune ?? e.title, read: e.read }, e.recipe)}
        >
          <strong>{e.title}</strong>
          <span>{e.note}</span>
        </Link>
      ))}
    </div>
  );
}

function Section({
  id,
  number,
  title,
  children,
}: {
  id: string;
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="drop2-section" aria-labelledby={`guide-${id}-heading`}>
      <div className="drop2-section-heading">
        <span aria-hidden="true">{number}</span>
        <div>
          <h2 id={`guide-${id}-heading`}>{title}</h2>
        </div>
      </div>
      {children}
    </section>
  );
}

const READING: { title: string; author: string; note: string }[] = [
  {
    title: 'Complete Book of Harmony, Theory & Voicing',
    author: 'Bret Willmott',
    note: 'The basis of the drop 2 method here: the four-part chord catalogue, top voices, the b9 rule and the studies.',
  },
  {
    title: 'Chord Chemistry and Modern Chord Progressions',
    author: 'Ted Greene',
    note: 'The V-System, which classifies every four-note spread, and voicing progressions across the neck.',
  },
  {
    title: 'Reharmonization Techniques',
    author: 'Randy Felts',
    note: 'The source for reharmonisation levels 1 to 7: diatonic approach, extended dominants, modal interchange, turnarounds, bass lines, line clichés and hybrids.',
  },
  {
    title: 'The Jazz Theory Book and The Jazz Piano Book',
    author: 'Mark Levine',
    note: 'Tritone substitution, the backdoor progression, Coltrane changes, upper structures and rootless piano voicings.',
  },
  {
    title: 'Building Walking Bass Lines',
    author: 'Ed Friedland',
    note: 'The bass styles, from roots only to two-note enclosures.',
  },
  {
    title: 'How to Comp, The Art of Comping, An Approach to Comping',
    author: 'Hal Crook, Jim McNeely, Jeb Patton',
    note: 'The comping rhythms: forward motion, rhythmic phrases and responding to the melody.',
  },
  {
    title: 'Voicings for Jazz Keyboard',
    author: 'Frank Mantooth',
    note: 'The Mantooth piano voicings: generic, Miracle and polychord voicings, and the right-thumb register rule.',
  },
  {
    title: 'Tunes You Thought You Knew (Aebersold Vol. 85)',
    author: 'Andy LaVerne',
    note: 'Reharmonised standards, compared bar by bar with the reharmonisation tool. The source for chords over their 5th and sus hybrids over a tonic.',
  },
  {
    title: 'The Chord Scale Theory & Jazz Harmony',
    author: 'Barrie Nettles and Richard Graf',
    note: 'The tensions each chord type allows, used to check suggested chords against the melody.',
  },
];

export function GuidePage() {
  return (
    <article className="drop2-page guide-page">
      <nav className="learn-breadcrumb" aria-label="Breadcrumb">
        <Link to={APP_ROUTES.learn}>Learn</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">How the app works</span>
      </nav>

      <header className="drop2-hero">
        <p className="learn-eyebrow">Guide</p>
        <h1>How the app works</h1>
        <p>
          The workbench shows a tune as a chart or score, plays it with a band, and generates
          voicings and reharmonisations. Each section below names the controls and links to a tune
          with them set.
        </p>
      </header>

      <nav className="drop2-toc" aria-label="On this page">
        <a href="#workbench">Workbench</a>
        <a href="#band">Band</a>
        <a href="#bass">Bass</a>
        <a href="#voicings">Voice leading</a>
        <a href="#melody">Chord melody</a>
        <a href="#reharm">Reharmonise</a>
        <a href="#reading">Reading</a>
        <a href="#books">Books</a>
      </nav>

      <Section id="workbench" number="01" title="The workbench">
        <p className="drop2-lede">
          The view bar has two groups. <strong>Chart</strong> or <strong>Score</strong> sets how the
          tune is shown. <strong>Off</strong>, <strong>Comping</strong> or{' '}
          <strong>Chord melody</strong> sets which chord diagrams are drawn with it. Any combination
          works: the chart with comping shows a diagram under every chord, and the score with
          comping shows the same diagrams above the notation.
        </p>
        <p>
          <strong>Tunes</strong> opens the search on the home page: chord charts, lead sheets,
          scores, saved tunes and playlists. <strong>Edit chords</strong> makes the chart editable.{' '}
          <kbd>⌘K</kbd> searches everything.
        </p>
        <Examples
          items={[
            {
              title: 'All Of Me',
              note: 'The chart only.',
              recipe: { view: 'chart', lens: 'none' },
            },
            {
              title: 'All Of Me, comping grips',
              tune: 'All Of Me',
              note: 'The same chart with a voice-led diagram under every chord.',
              recipe: { view: 'chart', lens: 'comping' },
            },
            {
              title: 'All Of Me, the score',
              tune: 'All Of Me',
              note: 'Notation, with the diagrams above each system.',
              recipe: { view: 'score', lens: 'comping' },
            },
          ]}
        />
      </Section>

      <Section id="band" number="02" title="Backing">
        <p className="drop2-lede">
          The bar at the bottom is the transport. Its chips show tempo, repeats, key, harmony and
          style. <strong>Style</strong> sets the whole band at once: swing, bossa, ballad, shuffle,
          waltz. The mixer icon opens the channels: <strong>Melody</strong>, <strong>Comp</strong>{' '}
          (instrument and rhythm), <strong>Bass</strong> and <strong>Drums</strong>, plus the
          metronome and count-in.
        </p>
        <p>
          The comp plays guitar or piano. The <strong>Chords</strong> menu sets which diagrams are
          drawn and the mixer sets which instrument plays. They are independent: guitar diagrams can
          be shown with a piano comp, and piano diagrams with a guitar comp.
        </p>
        <Examples
          items={[
            {
              title: 'Blue Bossa, bossa nova',
              tune: 'Blue Bossa',
              note: 'The bossa preset: tumbao bass, held chords, the Brazilian kit.',
              recipe: { style: 'bossa', lens: 'comping' },
            },
            {
              title: 'There Will Never Be Another You, piano comp',
              tune: 'There Will Never Be Another You',
              note: 'Piano comp with the Charleston rhythm, guitar diagrams on the sheet.',
              recipe: { backing: 'piano', comp: 'charleston', lens: 'comping', chords: 'guitar' },
            },
            {
              title: 'Satin Doll, piano diagrams',
              tune: 'Satin Doll',
              note: 'Keyboard voicings on the sheet, two-hand family.',
              recipe: { chords: 'piano', backing: 'piano', lens: 'comping' },
            },
          ]}
        />
      </Section>

      <Section id="bass" number="03" title="How the bass works">
        <p className="drop2-lede">
          The bass styles go from simple to complex: <strong>Roots</strong>,{' '}
          <strong>roots and fifths</strong>, a single approach note into each chord change
          (chromatic, dominant or scale), a full <strong>walking</strong> line, and{' '}
          <strong>advanced</strong>, which approaches each new root with a two-note enclosure. The
          feel is half notes or quarter notes. <strong>Heads in 2</strong> plays the head in half
          notes and the solos in quarter notes.
        </p>
        <p>
          The line is generated over the whole form and varies on each pass. Swing, ghost notes,
          anticipations and triplet drops are added only in playback; the line shown under the chart
          does not include them. Bossa and shuffle are separate groove patterns.
        </p>
        <Examples
          items={[
            {
              title: 'Autumn Leaves, walking',
              tune: 'Autumn Leaves',
              note: 'A walking line at medium swing.',
              recipe: { bass: 'walking', style: 'swing', lens: 'none' },
            },
            {
              title: 'Autumn Leaves, roots and fifths',
              tune: 'Autumn Leaves',
              note: 'Roots and fifths only, so the chord changes are easy to hear.',
              recipe: { bass: 'roots-fifths', style: 'swing', lens: 'none' },
            },
            {
              // The bundled score's own title: it carries the written cues,
              // and the openbook lead sheet of the same tune carries none.
              title: 'On Green Dolphin Street, Latin and swing',
              tune: 'On Green Dolphin Street',
              note: 'A tune with written feel changes: Latin in the A sections, swing in the bridge.',
              recipe: { lens: 'none' },
            },
          ]}
        />
      </Section>

      <Section id="voicings" number="04" title="Voice-leading options">
        <p className="drop2-lede">
          The <strong>Chords</strong> menu holds the voicing controls. <strong>Family</strong> is
          drop 2, drop 3 or both. <strong>Home register</strong> is the string set the line starts
          on. <strong>Allow layout changes</strong> lets it change string set to stay in the same
          position. <strong>Voice leading</strong> chooses the method and{' '}
          <strong>Opening top voice</strong> chooses the first inversion.
        </p>
        <p>
          <strong>Nearest grip</strong> picks the closest shape to the previous chord.{' '}
          <strong>Whole line</strong> finds the least total movement over the whole tune.{' '}
          <strong>Common tones</strong> keeps shared notes and measures movement in semitones; it
          differs from Whole line only when layout changes are allowed. <strong>Cycle form</strong>{' '}
          repeats the form until the first voicing returns. <strong>Unroll repeats</strong> writes
          the repeats out.
        </p>
        <Examples
          items={[
            {
              title: 'All Of Me, nearest grip',
              tune: 'All Of Me',
              note: 'Drop 2 on one string set, nearest grip.',
              recipe: { lens: 'comping', engine: 'pathfinder-v1', family: 'drop-2' },
            },
            {
              title: 'All Of Me, whole line',
              tune: 'All Of Me',
              note: 'The same tune with whole-line voice leading. Compare the total movement.',
              recipe: { lens: 'comping', engine: 'pathfinder-global-v1', family: 'drop-2' },
            },
            {
              title: 'All Of Me, drop 2 and drop 3 together',
              tune: 'All Of Me',
              note: 'Both families together; each diagram shows which family it uses.',
              recipe: { lens: 'comping', engine: 'pathfinder-global-v1', family: 'drop-2-3' },
            },
          ]}
        />
      </Section>

      <Section id="melody" number="05" title="Chord melody">
        <p className="drop2-lede">
          <strong>Chord melody</strong> puts the written melody note on top of each voicing, at its
          written octave, with the chord underneath. It needs a tune with a melody: lead sheets and
          scores have one, chord charts do not. The button is disabled for a tune without a melody.
        </p>
        <p>
          The melody note always stays on top. A melody tension changes the chord voicing (a natural
          11 over a major third replaces the third). When a note is out of reach, the whole phrase
          moves an octave rather than one note. On the score the diagrams sit above each system; on
          the chart they replace the chord symbols.
        </p>
        <Examples
          items={[
            {
              title: 'Stella By Starlight, chord melody',
              tune: 'Stella By Starlight',
              note: 'The melody as chord-melody diagrams, one per chord change.',
              recipe: { view: 'chart', lens: 'melody' },
            },
            {
              title: 'Misty, chord melody on the score',
              tune: 'Misty',
              note: 'Chord melody above the notation.',
              recipe: { view: 'score', lens: 'melody' },
            },
            {
              title: 'Fly Me To The Moon, piano chord melody',
              tune: 'Fly Me To The Moon',
              note: 'The melody note on top in the right hand, the root in the left.',
              recipe: { view: 'chart', lens: 'melody', chords: 'piano', backing: 'piano' },
            },
          ]}
        />
      </Section>

      <Section id="reharm" number="06" title="Reharmonise">
        <p className="drop2-lede">
          The <strong>Harmony</strong> chip opens the reharmonisation controls. With{' '}
          <strong>Reharmonise</strong> on, the chords are replaced according to the settings, and
          changes apply while the band plays. <strong>Up to</strong> sets the most complex level
          allowed: colour, same key, dominants, passing, borrowed, bass line, Coltrane, modern.{' '}
          <strong>How far out</strong> sets how unusual a change may be, compared with the library.{' '}
          <strong>How much</strong> sets the share of chords changed. <strong>Another</strong> gives
          a different result. Off returns to the original chords.
        </p>
        <p>
          <strong>Save</strong> stores the result as a named layer. The layer list switches between
          layers and the original. Every suggested chord is checked against the melody, and a chord
          that clashes with the melody note is not offered. The <strong>Analysis</strong> switch
          shows the key and a Roman numeral under each chord, on the chart and the score.
        </p>
        <Examples
          items={[
            {
              title: 'Autumn Leaves, dominants',
              tune: 'Autumn Leaves',
              note: 'Tritone substitutes and secondary dominants on half the chords, with analysis.',
              recipe: {
                reharm: { maxRung: 2, adventure: 'inside', density: 0.5 },
                analysis: true,
                lens: 'comping',
              },
            },
            {
              title: 'Fly Me To The Moon, borrowed and passing',
              tune: 'Fly Me To The Moon',
              note: 'Up to the bass-line level, stretched.',
              recipe: {
                reharm: { maxRung: 5, adventure: 'stretched', density: 0.6 },
                analysis: true,
                lens: 'comping',
              },
            },
            {
              title: 'Green Dolphin Street, modern',
              tune: 'Green Dolphin Street',
              note: 'Hybrid chords, modal cadences and constant structures, out.',
              recipe: {
                reharm: { maxRung: 7, adventure: 'out', density: 0.5 },
                analysis: true,
                lens: 'comping',
              },
            },
          ]}
        />
      </Section>

      <Section id="reading" number="07" title="Keys, readings and playlists">
        <p className="drop2-lede">
          The <strong>Key</strong> chip transposes the tune, shifts it by an octave, and sets the{' '}
          <strong>Reading</strong>: C, B♭, E♭ or bass clef, for transposing instruments. The band
          always plays in concert pitch. <strong>Export</strong> has the iReal Pro link, MusicXML,
          and <strong>Copy link</strong>, which links to the tune in the current reading.
        </p>
        <p>
          <strong>Playlists</strong> hold tunes in order, with a note on each row. <kbd>[</kbd> and{' '}
          <kbd>]</kbd> move to the previous and next tune. <strong>Auto</strong> plays the playlist
          through.
        </p>
        <Examples
          items={[
            {
              title: 'Take Five, in E♭ for an alto',
              tune: 'Take Five',
              read: 'eb',
              note: 'The E♭ reading for alto saxophone.',
              recipe: { view: 'chart', lens: 'none' },
            },
          ]}
        />
      </Section>

      <Section id="books" number="08" title="Recommended reading">
        <p className="drop2-lede">
          The app's rules come from these books. Each entry says which part of the app it informs.
        </p>
        <div className="learn-index-list guide-books" data-testid="guide-books">
          {READING.map((b) => (
            <div key={b.title} className="guide-book">
              <strong>
                {b.title}
                <small>{b.author}</small>
              </strong>
              <span>{b.note}</span>
            </div>
          ))}
        </div>
      </Section>
    </article>
  );
}
